import { BaseError } from 'libs/end-to-end/src/types/errors/base-error';
import { useCallback } from 'react';
import { useMemo } from 'react';
import { useRef } from 'react';
import { useState } from 'react';
import { IsAliveFn } from 'apps/sigma2-ui/src/lib/hooks/useAlive';
import { useAsyncEffect } from 'apps/sigma2-ui/src/lib/hooks/useAsyncEffect';
import { guardExhaustive } from 'libs/end-to-end/src/generic';
import { ReadFn } from 'apps/sigma2-ui/src/lib/read-write-patterns';

type HookStatus = 'loading' | 'done' | 'error';
type ReloadEntityFn = () => void;

interface ReadPatternLoadingHook {
  status: 'loading';
}

interface ReadPatternDoneHook<TRead> {
  status: 'done';
  entity: TRead;
  reloadEntity: ReloadEntityFn;
}

interface ReadPatternErrorHook<TRead> {
  status: 'error';
  entity: TRead | null;
  error: BaseError;
  reloadEntity: ReloadEntityFn;
}

export type ReadPatternHook<TRead> =
  | ReadPatternLoadingHook
  | ReadPatternDoneHook<TRead>
  | ReadPatternErrorHook<TRead>;

export const useReadPattern = <TRead>({
  isReadyToRead,
  readFn,
}: {
  isReadyToRead: boolean;
  readFn: ReadFn<TRead>;
}): ReadPatternHook<TRead> => {
  const entity = useRef<TRead | null>(null);
  const [status, setStatus] = useState<HookStatus>('loading');
  const [error, setError] = useState<BaseError | null>(null);

  const transitionToError = useCallback((error: BaseError) => {
    setStatus('error');
    setError(error);
  }, []);

  const transitionToDone = useCallback((loadedEntity: TRead) => {
    entity.current = loadedEntity;
    setStatus('done');
  }, []);

  const transitionToLoading = useCallback(() => {
    entity.current = null;
    setStatus('loading');
  }, []);

  const loadEntity = useCallback(() => {
    if (!isReadyToRead) {
      return;
    }

    transitionToLoading();
    readFn(
      (result) => {
        transitionToDone(result);
      },
      (error) => {
        transitionToError(error);
      },
    );
  }, [
    isReadyToRead,
    readFn,
    transitionToDone,
    transitionToError,
    transitionToLoading,
  ]);

  useAsyncEffect(
    // eslint-disable-next-line react-hooks/exhaustive-deps
    async (isAlive: IsAliveFn) => {
      if (!isAlive() || !isReadyToRead || status !== 'loading') {
        return;
      }

      loadEntity();
    },
    [isReadyToRead, loadEntity, status],
  );

  const reloadEntity = useCallback(() => {
    if (status !== 'done' && status !== 'error') {
      return;
    }

    transitionToLoading();
  }, [status, transitionToLoading]);

  return useMemo(() => {
    if (status === 'loading') {
      return { status };
    }

    if (status === 'error') {
      return {
        status,
        entity: entity.current,
        error: error ?? {
          errorType: 'UnknownError',
          errorReason: 'Unknown error',
        },
        reloadEntity,
      };
    }

    if (status === 'done') {
      if (!entity.current) {
        throw new Error('entity.current is required');
      }

      return {
        status,
        entity: entity.current,
        reloadEntity,
      };
    }

    throw guardExhaustive(status);
  }, [error, reloadEntity, status]);
};
